// The photo info panel of the viewer (#379): what the server stores with a photo (`PhotoInfo`, from its allowlist EXIF, see
// `photoOf` in exifAllow.ts) and the three lines every client draws from it. Clients never read EXIF. The record comes from a
// file, so it is untrusted: `cleanPhoto` checks every field again and leaves out one that fails, so a bad stored value shows
// nothing rather than nonsense. The lines are plain text: a client never draws them as HTML, markdown or a link. The Swift port
// (PhotoInfo.swift) and the Kotlin port read shared/cases/photo-info.json, as the tests here do.
import type { PhotoInfo } from "./protocol.ts";

/** The three lines of the panel. A line with no data is null. */
export interface PhotoLines {
  /** The camera and the lens: "iPhone 18 Pro · Front camera". */
  title: string | null;
  /** The shot settings: "f/1.9 · 1/39 s · ISO 320 · 30 mm". */
  settings: string | null;
  /** The time taken: "Oct 28, 2026 at 10:44 UTC", or "Oct 27, 2026 at 4:12 PM (camera clock)" when the photo has no offset. */
  taken: string | null;
}

const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const inRange = (v: unknown, lo: number, hi: number): v is number => isNumber(v) && v >= lo && v <= hi;
const isWhole = (v: unknown, lo: number, hi: number): v is number => inRange(v, lo, hi) && Number.isInteger(v);

/** Printable ASCII, 1 to 64 characters, no space at either end: what the sanitizer on upload leaves (media.md). */
const label = (v: unknown): string | undefined => (typeof v === "string" && /^[\x21-\x7e]([\x20-\x7e]{0,62}[\x21-\x7e])?$/.test(v) ? v : undefined);

const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const isLeap = (y: number) => y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);

/** "YYYY-MM-DDTHH:MM:SS" as numbers when it is a real time in 1900 to 2100. */
function parseTaken(v: unknown): [number, number, number, number, number, number] | undefined {
  if (typeof v !== "string") return;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(v);
  if (!m) return;
  const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [number, number, number, number, number, number];
  const days = mo >= 1 && mo <= 12 ? (mo === 2 && isLeap(y) ? 29 : DAYS[mo - 1]!) : 0;
  return y >= 1900 && y <= 2100 && d >= 1 && d <= days && h <= 23 && mi <= 59 && s <= 59 ? [y, mo, d, h, mi, s] : undefined;
}

/** "+HH:MM" or "-HH:MM" as minutes east of UTC, from -12:00 to +14:00. */
function parseOffset(v: unknown): number | undefined {
  if (typeof v !== "string") return;
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(v);
  if (!m || Number(m[3]) > 59) return;
  const minutes = (Number(m[2]) * 60 + Number(m[3])) * (m[1] === "+" ? 1 : -1);
  return minutes >= -720 && minutes <= 840 ? minutes : undefined;
}

/**
 * The record with every field checked again, or undefined when it has no camera info: no camera, lens or setting. The time alone
 * is not camera info. A field that fails its check is left out; a field the record does not define is ignored.
 */
export function cleanPhoto(raw: unknown): PhotoInfo | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return;
  const r = raw as Record<string, unknown>;
  const out: PhotoInfo = {};
  const camera = label(r.camera);
  if (camera !== undefined) out.camera = camera;
  const lens = label(r.lens);
  if (lens !== undefined) out.lens = lens;
  if (inRange(r.fNumber, 0.5, 64)) out.fNumber = r.fNumber;
  if (inRange(r.exposure, 1e-6, 3600)) out.exposure = r.exposure;
  if (isWhole(r.iso, 1, 10_000_000)) out.iso = r.iso;
  if (inRange(r.focal, 0.1, 5000)) out.focal = r.focal;
  if (isWhole(r.focal35, 1, 10_000)) out.focal35 = r.focal35;
  if (Object.keys(out).length === 0) return;
  if (parseTaken(r.takenAt)) {
    out.takenAt = r.takenAt as string;
    if (parseOffset(r.offset) !== undefined) out.offset = r.offset as string;
  }
  return out;
}

/** One decimal, rounded half up, with no ".0": 1.8994 is "1.9", 11 is "11". Whole numbers only in the arithmetic, so every client agrees. */
function oneDecimal(v: number): string {
  const tenths = Math.floor(v * 10 + 0.5);
  const whole = Math.floor(tenths / 10);
  const rest = tenths % 10;
  return rest === 0 ? String(whole) : `${whole}.${rest}`;
}

/**
 * 1/39 s for a fast shutter (1/4 s or faster), else seconds: "0.5 s", "2.5 s", "30 s". A time within 0.0005 s of 1/3 or 1/4
 * is that fraction, so a camera's 0.333333 s reads "1/3 s" and not "0.3 s" (#585); 0.3 s and 0.33 s stay decimals.
 */
function shutter(t: number): string {
  if (t <= 0.25) return `1/${Math.floor(1 / t + 0.5)} s`;
  const n = Math.floor(1 / t + 0.5);
  if (n >= 3 && Math.abs(t - 1 / n) <= 0.0005) return `1/${n} s`;
  return t >= 10 ? `${Math.floor(t + 0.5)} s` : `${oneDecimal(t)} s`;
}

/** An iPhone or iPad names its lens "iPhone 18 Pro front camera 2.715mm f/1.9": the camera already says the model, so "Front camera". */
function shortLens(lens: string): string {
  const m = /^(?:iPhone|iPad)\b.*?\b(front|back) (?:[A-Za-z]+ ){0,2}camera\b/.exec(lens);
  return m ? `${m[1]![0]!.toUpperCase()}${m[1]!.slice(1)} camera` : lens;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

/** The time taken. With an offset it is converted to UTC. Without one it is the camera's own clock, as recorded, labeled. */
function taken(p: PhotoInfo): string | null {
  const t = parseTaken(p.takenAt);
  if (!t) return null;
  const offset = parseOffset(p.offset);
  if (offset === undefined) {
    const [y, mo, d, h, mi] = t;
    return `${MONTHS[mo - 1]} ${d}, ${y} at ${h % 12 === 0 ? 12 : h % 12}:${pad(mi)} ${h < 12 ? "AM" : "PM"} (camera clock)`;
  }
  const u = new Date(Date.UTC(t[0], t[1] - 1, t[2], t[3], t[4], t[5]) - offset * 60_000);
  return `${MONTHS[u.getUTCMonth()]} ${u.getUTCDate()}, ${u.getUTCFullYear()} at ${pad(u.getUTCHours())}:${pad(u.getUTCMinutes())} UTC`;
}

/** The lines of the panel for a stored record, or null: no camera info, so no info button and no panel. */
export function formatPhotoInfo(raw: unknown): PhotoLines | null {
  const p = cleanPhoto(raw);
  if (!p) return null;
  const title = [p.camera, p.lens === undefined ? undefined : shortLens(p.lens)].filter((x): x is string => x !== undefined);
  const settings: string[] = [];
  if (p.fNumber !== undefined) settings.push(`f/${oneDecimal(p.fNumber)}`);
  if (p.exposure !== undefined) settings.push(shutter(p.exposure));
  if (p.iso !== undefined) settings.push(`ISO ${p.iso}`);
  // The 35 mm equivalent is what people compare; a camera with none shows the real focal length.
  if (p.focal35 !== undefined) settings.push(`${p.focal35} mm`);
  else if (p.focal !== undefined) settings.push(`${p.focal >= 10 ? Math.floor(p.focal + 0.5) : oneDecimal(p.focal)} mm`);
  return { title: title.length ? title.join(" · ") : null, settings: settings.length ? settings.join(" · ") : null, taken: taken(p) };
}
